import React, { useState, useEffect } from 'react';
import { X, Check, ArrowRight, Search, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { aiSearchQueries } from '../../data/mock';

const ProblemSection = ({ onGetAudit }) => {
  const [activeQuery, setActiveQuery] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const allQueries = aiSearchQueries.flatMap(cat => cat.queries);
    const currentQuery = allQueries[activeQuery % allQueries.length];
    
    let charIndex = 0;
    setIsTyping(true);
    setTypingText('');

    const typingInterval = setInterval(() => {
      if (charIndex <= currentQuery.length) {
        setTypingText(currentQuery.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
        setTimeout(() => {
          setActiveQuery(prev => prev + 1);
        }, 2000);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, [activeQuery]);

  const withoutOptimization = [
    'Invisible in AI search results (ChatGPT, Perplexity, Grok)',
    'Missing 73% of modern AI-powered search queries',
    'Competitors capturing your market share',
    'Declining organic visibility in traditional search',
    'No presence in voice search results'
  ];

  const withOptimization = [
    'Featured in ChatGPT & AI assistant recommendations',
    'Capturing AI-driven purchase intent from qualified leads',
    'First-mover advantage in your industry',
    'Growing authority across all AI platforms',
    'Voice search optimized content strategy'
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* AI Search Demo */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            AI Search in Action
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            When Your Customers Search,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500">
              Is Your Brand the Answer?
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Every day, millions of people ask AI assistants for recommendations. Here's what they're searching for:
          </p>
        </div>

        {/* AI Search Simulation */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
            {/* Browser Header */}
            <div className="bg-slate-800 px-6 py-3 flex items-center gap-4">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 bg-slate-700 rounded-lg px-4 py-1.5 flex items-center">
                <span className="text-slate-400 text-sm">ChatGPT / Perplexity / Grok</span>
              </div>
            </div>
            
            {/* Chat Interface */}
            <div className="p-8 space-y-6">
              {/* User Message */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 mb-2">User is asking...</p>
                  <div className="bg-slate-50 rounded-2xl rounded-tl-none p-4 inline-block">
                    <p className="text-lg text-slate-800 font-medium">
                      "{typingText}"
                      {isTyping && <span className="inline-block w-0.5 h-5 bg-violet-500 ml-1 animate-pulse" />}
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Response */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 mb-2">AI recommends...</p>
                  <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl rounded-tl-none p-6 border border-violet-100">
                    <p className="text-lg text-slate-800 font-medium">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 font-bold">Your Brand</span>{' '}
                      could be here!
                    </p>
                    <p className="text-slate-600 mt-2">
                      We help businesses become the #1 recommendation when customers search with AI.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Query Tags */}
            <div className="px-8 pb-8">
              <div className="flex flex-wrap gap-2">
                {aiSearchQueries.flatMap(cat => cat.queries).slice(0, 8).map((query, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all duration-300 cursor-pointer ${
                      index === activeQuery % 8
                        ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-lg'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setActiveQuery(index)}
                  >
                    {query}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Your Competitors Are Already{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500">
              Stealing Your Customers
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            While you focus on traditional SEO, AI assistants are recommending your competitors to millions of potential customers every day.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Without Optimization */}
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-lg relative overflow-hidden group hover:border-red-200 transition-all duration-300">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-orange-400" />
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-5 h-5 text-red-500" />
              </div>
              Without AI Optimization
            </h3>
            <ul className="space-y-4">
              {withoutOptimization.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* With Optimization */}
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-lg relative overflow-hidden group hover:border-emerald-200 transition-all duration-300">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-blue-400" />
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
              With PixZent AEO
            </h3>
            <ul className="space-y-4">
              {withOptimization.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            onClick={onGetAudit}
            className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white shadow-xl shadow-violet-500/25 text-lg px-8 py-6 group"
          >
            Start Your Free AI Audit Now
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-slate-500 mt-3 text-sm">Join 100+ brands already dominating AI search</p>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
